import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ImageProperties from '~/components/Properties/ImageProperties';
import ObjectDetailsDialog, {
    type ObjectDetailsDialogState,
} from '~/components/Properties/ObjectDetailsDialog';
import StructureSetProperties from '~/components/Properties/StructureSetProperties';
import StructureSetReviewStatus from '~/components/Structures/StructureSetReviewStatus';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { useAvailableStructureSetsView } from '~/hooks/domain/useAvailableStructureSetsView';
import { useLiveStructureSet } from '~/hooks/domain/useLiveStructureSet';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { seriesSummaryOf } from '~/utils/transforms/imageDataTransforms';
import {
    validateStructureSetDescription,
    validateStructureSetLabel,
} from '~/utils/validation';

const validateLabel = (value: string): string | null => {
    const [isValid, error] = validateStructureSetLabel(value.trim());
    return isValid ? null : error;
};

// StructureSetDescription is VR ST: line breaks and leading spaces are
// content, so the value is validated as typed.
const validateDescription = (value: string): string | null =>
    validateStructureSetDescription(value)[1];

/**
 * The details dialogs of the primary image and of the current structure
 * set, each with its Details and DICOM Tags tabs, open while their
 * `ObjectDetailsDialogState` says so.
 *
 * In the set's dialog the label and the description end with a pencil and
 * edit in place, unless the set is approved; neither can be edited while
 * patient info is hidden. The dialog's header states the set's approval
 * status. `renaming` opens the label's editor; closing the dialog closes
 * both editors.
 */
export default function ActiveDatasetDialogs({
    imageDialog,
    structureSetDialog,
    renaming,
    onRenamingChange,
}: {
    imageDialog: ObjectDetailsDialogState;
    structureSetDialog: ObjectDetailsDialogState;
    renaming: boolean;
    onRenamingChange: (renaming: boolean) => void;
}) {
    const intl = useIntl();
    const primaryImage = useSelectedPrimaryImage();
    const structureSet = useRecoilValue(currentStructureSetState);
    const structureSetView = useAvailableStructureSetsView(true).find(
        (set) => set.sopInstanceUid === structureSet?.dicomUid
    );
    const liveStructureSet = useLiveStructureSet(
        structureSetView?.structureSetId
    );
    const { setStructureSetLabel, setStructureSetDescription } =
        useStructureSet();
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const isApproved =
        structureSet?.approvalStatus === DICOMApprovalStatus.APPROVED;

    const rename = (newLabel: string) => {
        const trimmed = newLabel.trim();
        if (structureSet && trimmed !== structureSet.structureSetLabel) {
            void setStructureSetLabel(trimmed);
        }
        onRenamingChange(false);
    };

    // The editor submits only a changed value, with its trailing padding
    // already taken off; the line breaks it carries are kept.
    const describe = (newDescription: string) => {
        void setStructureSetDescription(newDescription);
        setIsEditingDescription(false);
    };

    const setDialog: ObjectDetailsDialogState = {
        ...structureSetDialog,
        close: () => {
            onRenamingChange(false);
            setIsEditingDescription(false);
            structureSetDialog.close();
        },
    };

    const imageSummary =
        primaryImage &&
        seriesSummaryOf(
            primaryImage,
            intl.formatMessage({ id: 'unknown.series' })
        );

    return (
        <>
            {primaryImage && imageSummary && (
                <ObjectDetailsDialog
                    dialog={imageDialog}
                    titleId="image"
                    name={imageSummary.label}
                    subtitle={`${imageSummary.modality} / ${intl.formatMessage(
                        { id: 'image.sliceCount' },
                        { count: primaryImage.length }
                    )}`}
                    dataset={primaryImage.firstSlice?.datasetWrapper}
                >
                    <ImageProperties image={primaryImage} />
                </ObjectDetailsDialog>
            )}
            {structureSet && structureSetView && (
                <ObjectDetailsDialog
                    dialog={setDialog}
                    titleId="structureSet"
                    name={structureSetView.displayName}
                    badges={
                        <StructureSetReviewStatus
                            id={structureSetView.structureSetId}
                        />
                    }
                    dataset={liveStructureSet?.dicomStructureSet.datasetWrapper}
                >
                    <StructureSetProperties
                        structureSet={structureSetView}
                        labelEdit={{
                            editable: !isApproved,
                            editing: renaming,
                            onEditingChange: onRenamingChange,
                            onSubmit: rename,
                            validate: validateLabel,
                        }}
                        descriptionEdit={{
                            editable: !isApproved,
                            editing: isEditingDescription,
                            onEditingChange: setIsEditingDescription,
                            onSubmit: describe,
                            validate: validateDescription,
                            multiline: true,
                        }}
                    />
                </ObjectDetailsDialog>
            )}
        </>
    );
}
